import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { apiClient } from '../apiClient';
import { useUser } from '../hooks/UserContext';
import { ExternalLink, Info } from 'lucide-react';

const RegisterProfile = () => {
  const [registrationType, setRegistrationType] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { refetchUser } = useUser();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setRegistrationType(queryParams.get('type') === 'invited' ? 'invited' : 'corporate');
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!fullName || !fullName.trim()) {
      setError('氏名を入力してください。');
      return;
    }
    if (fullName.trim().length > 50) {
      setError('氏名は50文字以内で入力してください。');
      return;
    }
    if (departmentName && departmentName.trim().length > 100) {
      setError('部署名は100文字以内で入力してください。');
      return;
    }
    if (!phoneNumber.match(/^\d{10,11}$/)) {
        setError('電話番号はハイフンなしの形式で入力してください（例: 09012345678）。');
        return;
    }
    if (registrationType === 'corporate') {
      if (!companyName || !companyName.trim()) {
        setError('企業名を入力してください。');
        return;
      }
      if (companyName.trim().length > 100) {
        setError('企業名は100文字以内で入力してください。');
        return;
      }
    }
    if (!agreed) {
      setError('利用規約等への同意が必要です。');
      return;
    }

    setLoading(true);

    let requestData = {
      representativeName: fullName.trim(),
      departmentName: departmentName ? departmentName.trim() : '',
      phoneNumber: phoneNumber.trim(),
      ...(registrationType === 'corporate' && { 
        companyName: companyName.trim(),
        registrationType: 'corporate' 
      }),
      ...(registrationType !== 'corporate' && { 
        companyName: fullName.trim(),
        registrationType: 'individual'
      })
    };

    if (registrationType === 'invited') {
      requestData = {
        representativeName: fullName.trim(),
        departmentName: departmentName ? departmentName.trim() : '',
        phoneNumber: phoneNumber.trim(),
      };
    }

    try {
      const response = await apiClient.post('/v1/register-user-profile', requestData);
      if (response.ok) {
        await refetchUser();
        window.location.href = '/dashboard';
      } else {
        const data = await response.json();
        setError(data.error || '登録に失敗しました。時間をおいて再度お試しください。');
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError('ネットワークエラーが発生しました。接続を確認して再度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const AgreementSection = () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
          <div style={{
              padding: '20px',
              border: '1px solid #bfdbfe',
              backgroundColor: '#eff6ff',
              borderRadius: '8px',
          }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '14px', color: '#1e40af', lineHeight: '1.5' }}>
                  <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      style={{ marginTop: '4px' }}
                      required
                  />
                  <span>
                      利用規約、個人情報保護方針および特定商取引法に基づく表記に同意します。<span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                          <Link to="/terms" target="_blank" style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '2px', textDecoration: 'underline' }}>
                              利用規約 <ExternalLink size={12} />
                          </Link>
                          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '2px', textDecoration: 'underline' }}>
                              個人情報保護方針 <ExternalLink size={12} />
                          </Link>
                          <Link to="/tokushoho" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '2px', textDecoration: 'underline' }}>
                              特定商取引法に基づく表記 <ExternalLink size={12} />
                          </Link>
                      </div>
                  </span>
              </label>
          </div>

          <div style={{
              display: 'flex',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#0369a1',
              lineHeight: '1.6'
          }}>
              <Info size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                  <p>ご登録いただくことで、Billwaveの利用規約およびプライバシーポリシーに同意したものとみなされます。</p>
                  <p>ご不明な点がございましたら、サポートまでお問い合わせください。</p>
              </div>
          </div>
      </div>
  );

  if (registrationType === null) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>読み込み中...</div>;
  }

  if (registrationType === 'invited') {
    return (
        <div style={{ width: '100%', maxWidth: '600px', margin: 'auto', padding: '20px' }}>
            <h1 style={{
              fontSize: '1.875rem',
              fontWeight: 'bold',
              textAlign: 'center',
              color: '#111827',
              marginBottom: '1.5rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid #e5e7eb'
            }}>プロフィール登録</h1>
    
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                <div>
                  <label htmlFor="departmentName" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>部署名</label>
                  <input
                    id="departmentName"
                    type="text"
                    value={departmentName}
                    onChange={(e) => setDepartmentName(e.target.value)}
                    style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label htmlFor="fullName" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>氏名 <span style={{color: 'red'}}>*</span></label>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label htmlFor="phoneNumber" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>電話番号 <span style={{color: 'red'}}>*</span></label>
                  <input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="例: 09012345678"
                    required
                    pattern="\d{10,11}"
                    title="電話番号はハイフンなしで入力してください。"
                    style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
    
                <AgreementSection />

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '15px',
                    fontSize: '18px',
                    cursor: 'pointer',
                    backgroundColor: loading ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    marginTop: '10px'
                  }}
                >
                  {loading ? '登録中...' : '登録完了'}
                </button>
    
                {error && <p style={{ color: 'red', textAlign: 'center', marginTop: '1rem' }}>{error}</p>}
            </form>
        </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '600px', margin: 'auto', padding: '20px' }}>
      <h1 style={{
        fontSize: '1.875rem',
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#111827',
        marginBottom: '1rem',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid #e5e7eb'
      }}>会員情報登録</h1>

      <div style={{
        backgroundColor: '#fffbeb',
        border: '1px solid #fef3c7',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '1.5rem',
        fontSize: '14px',
        color: '#92400e',
        lineHeight: '1.5',
        textAlign: 'center'
      }}>
        Billwaveは日本国内の法人・事業者を対象として提供しています。日本国外の法人・事業者によるご利用は現在受け付けておりません。
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '1rem' }}>
          <label style={{cursor: 'pointer'}}>
            <input
              type="radio"
              name="registrationType"
              value="corporate"
              checked={registrationType === 'corporate'}
              onChange={() => setRegistrationType('corporate')}
              style={{marginRight: '5px'}}
            />
            法人
          </label>
          <label style={{cursor: 'pointer'}}>
            <input
              type="radio"
              name="registrationType"
              value="individual"
              checked={registrationType === 'individual'}
              onChange={() => setRegistrationType('individual')}
              style={{marginRight: '5px'}}
            />
            個人
          </label>
        </div>

        {registrationType === 'corporate' && (
          <>
            <div>
              <label htmlFor="companyName" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>企業名 <span style={{color: 'red'}}>*</span></label>
              <input
                id="companyName"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label htmlFor="departmentName" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>部署名</label>
              <input
                id="departmentName"
                type="text"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </>
        )}

        <div>
          <label htmlFor="fullName" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>氏名 <span style={{color: 'red'}}>*</span></label>
          <input
            id="fullName"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <div>
          <label htmlFor="phoneNumber" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>電話番号 <span style={{color: 'red'}}>*</span></label>
          <input
            id="phoneNumber"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="例: 09012345678"
            required
            pattern="\d{10,11}"
            title="電話番号はハイフンなしで入力してください。"
            style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>

        <AgreementSection />

        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '15px',
            fontSize: '18px',
            cursor: 'pointer',
            backgroundColor: loading ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            marginTop: '10px'
          }}
        >
          {loading ? '登録中...' : '登録完了'}
        </button>

        {error && <p style={{ color: 'red', textAlign: 'center', marginTop: '1rem' }}>{error}</p>}
      </form>
    </div>
  );
};

export default RegisterProfile;
